<script setup>
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import Message from "primevue/message";
import InputNumber from "primevue/inputnumber";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import { computed, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";

const props = defineProps([
    'device',
    'companies',
    'services',
    'flash',
]);

const route = window.route;
const toast = useToast();
const errors = ref({});
const filteredServices = ref(props.services);

const uniqueByKey = (items, key) => Array.from(new Map(items.map((item) => [item[key], item])).values());

const statusOptions = [
    { label: 'Verfügbar', value: 'available' },
    { label: 'Wartung', value: 'maintenance' },
    { label: 'Defekt', value: 'out_of_service' },
];

const resourceTypeOptions = [
    { label: 'Kein Typ', value: null },
    { label: 'Frauen', value: 'female' },
    { label: 'Männer', value: 'male' },
];

const form = useForm({
    company_id: props.device.company_id,
    name: props.device.name,
    name_de: props.device.name_de,
    description: props.device.description,
    manufacturer_page: props.device.manufacturer_page,
    number: props.device.number,
    combustion_per_session: props.device.combustion_per_session,
    time: props.device.time,
    json: props.device.json || {},
    resources: props.device.resources ? [...props.device.resources] : [],
    service_requirements: props.device.service_requirements ? uniqueByKey(props.device.service_requirements.map((item) => ({
        service_id: item.service_id,
        is_required: !!item.is_required,
        quantity: item.quantity || 1,
    })), 'service_id') : [],
    allow_resource_selection: props.device.allow_resource_selection ?? false,
});

const addResource = () => {
    const deviceName = form.name_de || 'Einheit';
    form.resources.push({
        id: null,
        uuid: null,
        name: `${deviceName} #${form.resources.length + 1}`,
        code: '',
        status: 'available',
        resource_type: null
    });
};

const removeResource = (index) => {
    form.resources.splice(index, 1);
};

watch(() => form.company_id, (newId) => {
    if (newId) {
        filteredServices.value = props.services.filter((service) => service.company_id === newId);
    } else {
        filteredServices.value = props.services;
    }
}, { immediate: true });

const addServiceRequirement = () => {
    form.service_requirements.push({
        service_id: null,
        is_required: true,
        quantity: 1,
    });
};

const removeServiceRequirement = (index) => {
    form.service_requirements.splice(index, 1);
};

const availableServicesForRow = (rowIndex) => {
    const currentId = form.service_requirements[rowIndex]?.service_id;

    return filteredServices.value.filter((service) => {
        if (service.id === currentId) {
            return true;
        }

        return !form.service_requirements.some((row, index) => index !== rowIndex && row.service_id === service.id);
    });
};

const submit = () => {
    form.put(`/management/devices/${props.device.uuid}`, {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Gerät wurde erfolgreich aktualisiert.',
                life: 5000,
            });
        },
        onError: (err) => {
            errors.value = err;
            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben.',
                life: 5000,
            });
        }
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));

</script>

<template>
    <Layout :flash="flash">
        <Head title="Gerät bearbeiten" />

        <div class="text-sm">
            <div class="flex items-center gap-4 mb-8">
                <Link href="/management/devices" class="p-2 rounded-full hover:bg-gray-100 transition-colors">
                    <i class="pi pi-arrow-left text-gray-500" />
                </Link>
                <h1 class="text-2xl font-bold text-gray-800">Gerät bearbeiten: <span class="text-gray-500 font-normal">{{ device.name_de }}</span></h1>
            </div>

            <Message v-if="hasErrors" severity="error" class="mb-6">
                <template v-for="(error, key) in errors" :key="key">
                    <div v-if="error">{{ Array.isArray(error) ? error[0] : error }}</div>
                </template>
            </Message>

            <form @submit.prevent="submit" class="space-y-8">
                <!-- Section 1: Basic Info -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">1</div>
                        <h2 class="text-lg font-bold text-gray-700">Grundinformationen</h2>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div v-if="$page.props.auth.user.role === 'super_admin'" class="flex flex-col gap-2">
                            <label for="company_id" class="font-bold text-gray-600">Unternehmen *</label>
                             <Dropdown id="company_id" v-model="form.company_id" :options="companies" optionLabel="name" optionValue="id" 
                                       placeholder="Unternehmen wählen" :class="{'p-invalid': errors.company_id}" filter class="w-full" @change="errors.company_id = null" />
                             <small v-if="errors.company_id" class="p-error">{{ errors.company_id }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="number" class="font-bold text-gray-600">Seriennummer / Kennung</label>
                            <InputText id="number" v-model="form.number" class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="name_de" class="font-bold text-gray-600">Name (Deutsch) *</label>
                             <InputText id="name_de" v-model="form.name_de" :class="{'p-invalid': errors.name_de}" class="w-full" @input="errors.name_de = null" />
                             <small v-if="errors.name_de" class="p-error">{{ errors.name_de }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="name" class="font-bold text-gray-600">Name (Englisch) *</label>
                            <InputText id="name" v-model="form.name" :class="{'p-invalid': errors.name}" class="w-full" @input="errors.name = null" />
                            <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                        </div>
                        
                        <div class="flex flex-col gap-2">
                            <label class="font-bold text-gray-600">Auswahl von Einheiten erlauben</label>
                            <div class="flex items-center gap-3">
                                <Checkbox v-model="form.allow_resource_selection" :binary="true" />
                                <small class="text-gray-400">Erlaubt die Auswahl einer konkreten Einheit bei der Buchung.</small>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section 2: Technical Specs -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">2</div>
                        <h2 class="text-lg font-bold text-gray-700">Technische Details</h2>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div class="flex flex-col gap-2">
                            <label for="time" class="font-bold text-gray-600">Standardzeit (Minuten)</label>
                            <InputText id="time" v-model="form.time" class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="combustion" class="font-bold text-gray-600">Verbrennung pro Sitzung (kcal)</label>
                            <InputNumber id="combustion" v-model="form.combustion_per_session" :min="0" class="w-full" inputClass="w-full" showButtons />
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="manufacturer" class="font-bold text-gray-600">Hersteller Webseite</label>
                            <InputText id="manufacturer" v-model="form.manufacturer_page" class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="description" class="font-bold text-gray-600">Beschreibung</label>
                            <Textarea id="description" v-model="form.description" rows="3" class="w-full" />
                        </div>
                    </div>
                </div>

                <!-- Section 3: Resources Management -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center justify-between mb-6">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">3</div>
                            <h2 class="text-lg font-bold text-gray-700">Ressourcen (Einzelne Einheiten)</h2>
                        </div>
                        <Button type="button" @click="addResource" label="Einheit hinzufügen" icon="pi pi-plus" size="small" severity="secondary" outlined />
                    </div>
                    
                    <DataTable :value="form.resources" class="p-datatable-sm" responsiveLayout="scroll">
                        <Column header="Name *" style="width: 40%">
                            <template #body="{ data, index }">
                                <InputText v-model="data.name" 
                                           :class="{'p-invalid': errors['resources.' + index + '.name']}" 
                                           class="w-full p-inputtext-sm" 
                                           placeholder="z.B. Zimmer 1"
                                           @input="errors['resources.' + index + '.name'] = null" />
                                <small v-if="errors['resources.' + index + '.name']" class="p-error">
                                    {{ Array.isArray(errors['resources.' + index + '.name']) ? errors['resources.' + index + '.name'][0] : errors['resources.' + index + '.name'] }}
                                </small>
                            </template>
                        </Column>
                        <Column header="Kennung / Code">
                            <template #body="{ data }">
                                <InputText v-model="data.code" class="w-full p-inputtext-sm" placeholder="z.B. UNIT-01" />
                            </template>
                        </Column>
                        <Column header="Typ">
                            <template #body="{ data }">
                                <Dropdown v-model="data.resource_type" :options="resourceTypeOptions" optionLabel="label" optionValue="value" class="w-full p-inputtext-sm" placeholder="Wählen" />
                            </template>
                        </Column>
                        <Column header="Status">
                            <template #body="{ data }">
                                <Dropdown v-model="data.status" :options="statusOptions" optionLabel="label" optionValue="value" class="w-full p-inputtext-sm" />
                            </template>
                        </Column>
                        <Column headerStyle="width: 3rem">
                            <template #body="{ index }">
                                <Button icon="pi pi-times" severity="danger" text rounded @click="removeResource(index)" />
                            </template>
                        </Column>
                    </DataTable>
                    <div v-if="form.resources.length === 0" class="text-center py-8 bg-white rounded-lg border border-dashed border-gray-200 mt-2">
                        <p class="text-gray-400">Keine Einheiten definiert. Klicken Sie auf "Einheit hinzufügen", um Ressourcen für dieses Gerät zu erstellen.</p>
                    </div>
                </div>

                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center justify-between mb-6">
                        <div>
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">4</div>
                                <h2 class="text-lg font-bold text-gray-700">Service-Anforderungen</h2>
                            </div>
                            <p class="text-xs text-gray-400 mt-2">Welche Dienstleistungen dieses Gerät benötigt oder unterstützt.</p>
                        </div>
                        <Button type="button" @click="addServiceRequirement" label="Anforderung hinzufügen" icon="pi pi-plus" size="small" severity="secondary" outlined />
                    </div>

                    <DataTable :value="form.service_requirements" class="p-datatable-sm" responsiveLayout="scroll">
                        <Column header="Dienstleistung *">
                            <template #body="{ data, index }">
                                <Dropdown v-model="data.service_id" :options="availableServicesForRow(index)" optionLabel="name" optionValue="id" placeholder="Dienstleistung wählen" class="w-full p-inputtext-sm" filter />
                            </template>
                        </Column>
                        <Column header="Pflicht" headerStyle="width: 6rem">
                            <template #body="{ data }">
                                <Checkbox v-model="data.is_required" :binary="true" />
                            </template>
                        </Column>
                        <Column header="Menge" headerStyle="width: 8rem">
                            <template #body="{ data }">
                                <InputNumber v-model="data.quantity" :min="1" class="w-full" inputClass="w-full p-inputtext-sm" showButtons />
                            </template>
                        </Column>
                        <Column headerStyle="width: 3rem">
                            <template #body="{ index }">
                                <Button icon="pi pi-times" severity="danger" text rounded @click="removeServiceRequirement(index)" />
                            </template>
                        </Column>
                    </DataTable>

                    <div v-if="form.service_requirements.length === 0" class="text-center py-8 bg-white rounded-lg border border-dashed border-gray-200 mt-2">
                        <p class="text-gray-400">Keine Service-Anforderungen definiert.</p>
                    </div>
                </div>

                <!-- Footer Actions -->
                <div class="flex justify-between items-center pt-8 border-t mt-10">
                    <Button asChild v-slot="slotProps" severity="secondary" outlined>
                        <Link href="/management/devices" :class="slotProps.class" class="px-8 py-3 rounded-xl border-gray-300 font-bold">
                            Abbrechen
                        </Link>
                    </Button>

                    <Button type="submit" 
                            class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                            :loading="form.processing" :disabled="form.processing">
                        <i class="pi pi-check" />
                        Änderungen speichern
                    </Button>
                </div>
            </form>
        </div>
    </Layout>
</template>
